import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { verifyProfilePw } from "@/services/ccApi";

// Generic profile-password gate. gate = { title, description, needReason, reasonLabel, confirmLabel, run(reason) }
export function PasswordGate({ gate, onClose }) {
  const [pw, setPw] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!gate) return null;
  const close = () => { setPw(""); setReason(""); onClose(); };
  const go = async () => {
    if (gate.needReason && reason.trim().length < 5) return toast.error("Reason required (min 5 characters)");
    setBusy(true);
    try {
      const r = await verifyProfilePw(pw).catch((e) => ({ ok: false, err: e.response?.data?.detail }));
      if (!r.ok) { toast.error(r.err || "Incorrect profile password"); return; }
      await gate.run(reason.trim());
      close();
    } catch (e) { toast.error(e?.code === "permission-denied" ? "Permission denied by Firestore rules" : e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="password-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{gate.title}</DialogTitle>
          {gate.description && <DialogDescription>{gate.description}</DialogDescription>}
        </DialogHeader>
        {gate.needReason !== undefined && (
          <div className="space-y-1.5"><Label>{gate.reasonLabel || "Reason"}{gate.needReason ? " *" : " (optional)"}</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="gate-reason" /></div>
        )}
        <div className="space-y-1.5"><Label>Profile password *</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && pw && go()} data-testid="gate-password" /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} data-testid="gate-cancel">Cancel</Button>
          <Button variant={gate.destructive ? "destructive" : "default"} disabled={!pw || busy} onClick={go} data-testid="gate-confirm">{busy ? "Working…" : gate.confirmLabel || "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
